import type { MachineDef, MasterDie, Rarity } from '../../../core/types';
import { dieScore } from '../../../core/dice';
import { GACHA_LOTS, effectiveWeights, isMachineUnlocked, recycleValue } from '../../../core/gacha';
import { MACHINES, RARITY_NAME, RARITY_ORDER } from '../../../data/machines';
import { BALANCE } from '../../../data/balance';
import { NODE_BY_ID } from '../../../data/nodes';
import type { ScreenCtx } from '../../types';
import {
  corruptedMachinesOf,
  corruptedPurchasesOf,
  corruptedUnitCostOf,
  hasForge,
  isCorruptedMachine,
} from '../../abyssApi';
import { dieCardInnerHtml, dieSidesLabel, formatGold, formatNumber } from '../../format';
import { dieArt } from '../../diceArt';
import { icon } from '../../icons';
import { dieSpellsOf } from '../../spellsApi';
import { overlayRoot } from '../../columnScale';
import { registerEscape } from '../../keyboard';
import { toast } from '../../toast';
import { equipRefusal } from '../../equipApi';
import { readSessionPref, writeSessionPref } from '../../sessionPref';
import { groupRevealByRarity } from '../../diceTabs';
import { buildSubtabNav } from './subtabs';
import { isEndless } from '../../../core/world';

/**
 * Onglet « Boutique » de l'Accalmie — refonte F1, lot 2 (v0.15.0).
 *
 * Une **carte facettée** par machine (rendue depuis `src/data/machines.ts`,
 * aucune liste en dur) : vignette de dé SVG (`ui/diceArt.ts`) liserée de la
 * meilleure rareté que la machine peut donner, prix par dé, barre des cotes de
 * rareté, grille des lots en pierre (désactivés si l'or manque). Machine
 * verrouillée : carte assombrie, cadenas, « Débloquer dans l'Arbre (nœud) ».
 * Puis « Recycler tous les Communs (+N or) » une fois le Recycleur acheté.
 *
 * v0.46.0 (A94) : sous les machines classiques, la section **Corrupted
 * GachaDice** (visible seulement avec la Forge, `hasForge()`) : une carte par
 * machine `corrupted`, mêmes composants, liseré violet, prix indexé sur le
 * niveau (« N or le dé · 1 Expédition du niveau L »), compteur permanent de dés
 * tirés ici. La révélation montre le niveau, le Matériau et « Enrichi ».
 *
 * v0.49.0 (A98) : avec la Forge (`hasForge()`), la Boutique se divise en deux
 * **sous-onglets** en tête : « Normal » (les machines classiques et le Recycleur,
 * comme avant) à gauche, « Corrupted » (la section Corrupted seule) à droite.
 * Le sous-onglet choisi est gardé le temps de la session (`sessionStorage`,
 * repli en mémoire). Sans la Forge : aucun sous-onglet, l'écran est inchangé.
 *
 * Révélation du tirage : modale F1 (`.f-modal`), cartes des dés obtenus (nom
 * coloré par rareté, badge, faces, affixes, sorts), « Équiper » avec choix
 * d'emplacement (A32), « Fermer » ; les Communs d'un
 * gros tirage se rangent en lignes compactes. Apparition sobre (fondu et
 * glissement), coupée en mouvement réduit.
 *
 * v0.52.0 (COMMENTS v0.51.0 §2, A102) : les dés obtenus sont rangés en
 * **volets dépliables par rareté** (`<details data-role="reveal-rarity-panel">`,
 * du plus rare au plus commun, `ui/diceTabs.ts : groupRevealByRarity` ; le
 * volet le plus rare ouvert (A122 : plus de badge « Meilleur » ni de bouton « Équiper le meilleur »), l'ordre du cœur conservé
 * dans chaque volet). Le choix d'emplacement montre, sous chaque emplacement
 * occupé, la **carte du dé équipé** à remplacer (sorts, niveau, Matériau,
 * affixes, rareté, type de dé — `dieCardInnerHtml`).
 */

/** Cotes effectives d'une machine, en pourcentages (raretés > 0). */
function odds(machine: MachineDef, rarityMult: number): { rarity: Rarity; pct: number; weight: number }[] {
  const weights = effectiveWeights(machine, rarityMult);
  const total = RARITY_ORDER.reduce((sum, r) => sum + (weights[r] ?? 0), 0) || 1;
  return RARITY_ORDER.filter((r) => (weights[r] ?? 0) > 0).map((r) => ({
    rarity: r,
    weight: weights[r] ?? 0,
    pct: ((weights[r] ?? 0) / total) * 100,
  }));
}

/**
 * Pourcentage affiché : entier dès 1 %, une décimale en dessous — la *GachaDice
 * v20* (v0.26.0) annonce 0,9 % de Légendaire et 0,1 % de Prismatique.
 */
function pctText(pct: number): string {
  if (pct >= 1) return String(Math.round(pct));
  const one = Math.round(pct * 10) / 10;
  return one > 0 ? String(one).replace('.', ',') : '<0,1';
}

function oddsHtml(machine: MachineDef, rarityMult: number): string {
  const list = odds(machine, rarityMult);
  const bar = list
    // Plancher visuel : une rareté à 0,1 % reste une tranche visible de la barre.
    .map((o) => `<i data-rarity="${o.rarity}" style="flex:${Math.max(o.weight, 0.6).toFixed(3)} 1 0"></i>`)
    .join('');
  const text = list.map((o) => `${pctText(o.pct)} % ${RARITY_NAME[o.rarity]}`).join(' · ');
  return `<div class="machine-odds"><div class="machine-odds-bar" aria-hidden="true">${bar}</div><p class="machine-odds-text">${text}</p></div>`;
}

/** Meilleure rareté que la machine peut donner (liseré de la vignette). */
function topRarity(machine: MachineDef, rarityMult: number): Rarity {
  const list = odds(machine, rarityMult);
  return list.length ? list[list.length - 1].rarity : 'common';
}

/**
 * Choix d'emplacement (A32) : une entrée par emplacement débloqué, portant le
 * dé actuellement en place (ou « Vide »). Valider **remplace** — l'ancien dé
 * retourne à l'inventaire, comportement déjà assuré par `game.equipDie`
 * (identique à l'onglet Dés, `screens/accalmie/dice.ts`).
 *
 * v0.52.0 (A102) : sous le bouton d'un emplacement occupé, la **carte du dé
 * équipé** qui serait remplacé (`dieCardInnerHtml` : nom, type de dé, rareté,
 * niveau, Matériau, Enrichi, affixes, sorts), pour choisir en connaissance de
 * cause. Un emplacement vide n'a pas de carte.
 */
function buildSlotPicker(ctx: ScreenCtx, die: MasterDie, onDone: () => void): HTMLElement {
  const { game } = ctx;
  const wrap = document.createElement('ul');
  wrap.className = 'reveal-slot-picker';
  wrap.setAttribute('data-role', 'slot-picker');
  const slots = game.state.equipped;
  for (let i = 0; i < slots.length; i++) {
    const occupantId = slots[i];
    const occupant = occupantId ? game.state.inventory.find((d) => d.id === occupantId) : null;
    const item = document.createElement('li');
    item.className = 'reveal-slot';
    item.setAttribute('data-role', 'reveal-slot');
    item.dataset.slot = String(i);
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'f-btn f-btn--block f-btn--slot';
    btn.setAttribute('data-action', 'equip-slot');
    btn.setAttribute('data-slot', String(i));
    btn.innerHTML = `<span class="reveal-slot-label">Emplacement ${i + 1} — ${occupant ? occupant.name : 'Vide'}</span><span class="reveal-slot-verb">${occupant ? 'Remplacer' : 'Équiper'}</span>`;
    btn.addEventListener('click', () => {
      // Un seul exemplaire de chaque dé nommé équipé à la fois (A72-14).
      const refusal = equipRefusal(game, die.id, i);
      if (refusal) {
        toast(refusal, 2600, 'lock');
        return;
      }
      game.equipDie(die.id, i);
      onDone();
    });
    item.appendChild(btn);
    if (occupant) {
      const card = document.createElement('div');
      card.className = 'die-card reveal-slot-die f-cut';
      card.setAttribute('data-role', 'reveal-slot-die');
      card.dataset.rarity = occupant.rarity;
      card.dataset.dieId = occupant.id;
      card.innerHTML = dieCardInnerHtml(occupant, '', game);
      item.appendChild(card);
    }
    wrap.appendChild(item);
  }
  return wrap;
}

function openRevealModal(ctx: ScreenCtx, dice: MasterDie[], refresh: () => void): void {
  const { game } = ctx;
  // Les Communs d'un tirage de plusieurs dés se rangent en lignes compactes
  // (maquette F1) ; un tirage à l'unité garde sa carte complète.
  const compactCommons = dice.length > 2;

  const overlay = document.createElement('div');
  overlay.className = 'f-scrim reveal-overlay';
  overlay.innerHTML = `
    <div class="f-modal reveal-modal" role="dialog" aria-modal="true" aria-labelledby="reveal-title">
      <h2 class="f-modal-title" id="reveal-title" data-role="reveal-title">Nouveaux dés (${dice.length})</h2>
      <div class="reveal-list is-revealing" data-role="reveal-list"></div>
      <div class="f-modal-actions reveal-actions">
        <button type="button" class="f-btn f-btn--ghost" data-action="close">Fermer</button>
      </div>
    </div>
  `;
  const list = overlay.querySelector<HTMLElement>('.reveal-list');

  // Dé dont le choix d'emplacement est déplié (un seul à la fois) — COMMENTS
  // v0.7.0 §3 : « ce nouveau bouton demande l'emplacement que le dé doit prendre ».
  let openPickerFor: string | null = null;
  // Volets ouverts ou fermés par le joueur dans cette fenêtre (A102) : un
  // nouveau rendu (après un équipement) ne les referme pas.
  const panelOpen = new Map<Rarity, boolean>();

  function equipButton(die: MasterDie, compact: boolean): HTMLButtonElement {
    const equipBtn = document.createElement('button');
    equipBtn.type = 'button';
    const open = openPickerFor === die.id;
    equipBtn.className = `f-btn ${compact ? 'f-btn--ghost reveal-row-equip' : ''} f-btn--sm`;
    equipBtn.setAttribute('data-action', 'equip-die');
    equipBtn.setAttribute('aria-expanded', String(open));
    equipBtn.textContent = 'Équiper';
    equipBtn.addEventListener('click', () => {
      openPickerFor = openPickerFor === die.id ? null : die.id;
      renderList(false);
    });
    return equipBtn;
  }

  function pickerFor(die: MasterDie): HTMLElement {
    return buildSlotPicker(ctx, die, () => {
      openPickerFor = null;
      renderList(false);
      // La carte équipée derrière la modale (onglet Dés, statistiques…)
      // doit refléter le nouvel emplacement sans attendre la fermeture.
      refresh();
    });
  }

  function buildItem(die: MasterDie, index: number): HTMLLIElement {
    const li = document.createElement('li');
    const compact = compactCommons && die.rarity === 'common';
    li.dataset.rarity = die.rarity;
    li.style.setProperty('--i', String(Math.min(index, 8)));
    const equippedSlot = game.state.equipped.indexOf(die.id);
    const equipped =
      equippedSlot >= 0
        ? `<p class="die-card-equipped" data-role="reveal-equipped">${icon('check')}<span>Équipé · emplacement ${equippedSlot + 1}</span></p>`
        : '';
    if (compact) {
      li.className = 'reveal-item reveal-item--compact f-cut';
      const spells = dieSpellsOf(die);
      li.innerHTML = `
        <div class="reveal-row">
          ${dieArt(die.sides, die.rarity, { size: 24, stroke: 3 })}
          <span class="die-card-name">${die.name}</span>
          ${spells.length ? `<span class="reveal-row-spell">${icon('spell')}${spells.map((s) => s.name).join(', ')}</span>` : ''}
          <span class="f-badge" data-rarity="${die.rarity}">${RARITY_NAME[die.rarity]}</span>
        </div>${equipped}`;
      li.querySelector('.reveal-row')?.appendChild(equipButton(die, true));
    } else {
      li.className = `reveal-item die-card f-cut`;
      li.innerHTML = dieCardInnerHtml(die, '', game) + equipped;
      const actions = document.createElement('div');
      actions.className = 'die-card-actions';
      actions.appendChild(equipButton(die, false));
      li.appendChild(actions);
    }
    if (openPickerFor === die.id) li.appendChild(pickerFor(die));
    return li;
  }

  function renderList(first: boolean): void {
    if (!list) return;
    list.innerHTML = '';
    list.classList.toggle('is-revealing', first);
    // COMMENTS v0.6.0 §2 : « les dés obtenus par une machine Gachapon sont
    // triés par ordre de rareté » — le tri est fait par le cœur (`buyGacha`) ;
    // A102 : un volet par rareté, du plus rare au plus commun ; A103 : dans
    // chaque volet, niveau décroissant puis faces puis score (tri par défaut).
    const stats = game.getDerivedStats();
    let index = 0;
    for (const panel of groupRevealByRarity(dice, (d) => dieScore(d, stats))) {
      const details = document.createElement('details');
      details.className = 'f-panel f-details rarity-panel reveal-panel';
      details.setAttribute('data-role', 'reveal-rarity-panel');
      details.dataset.rarity = panel.rarity;
      details.open = panelOpen.get(panel.rarity) ?? panel.open;
      const summary = document.createElement('summary');
      summary.className = 'rarity-panel-title';
      summary.innerHTML = `<span class="rarity-panel-name" data-rarity="${panel.rarity}">${panel.title}</span>`;
      details.appendChild(summary);
      details.addEventListener('toggle', () => panelOpen.set(panel.rarity, details.open));
      const items = document.createElement('ul');
      items.className = 'reveal-panel-list';
      for (const die of panel.dice) items.appendChild(buildItem(die, index++));
      details.appendChild(items);
      list.appendChild(details);
    }
  }
  renderList(true);

  // `Échap` passe par la pile de `ui/keyboard.ts` (la fenêtre la plus récente
  // se ferme d'abord).
  let unregisterEscape = (): void => {};
  function close(): void {
    unregisterEscape();
    overlay.remove();
  }
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('[data-action="close"]')?.addEventListener('click', close);

  overlayRoot().appendChild(overlay);
  unregisterEscape = registerEscape(overlay, close);
  overlay.querySelector<HTMLElement>('[data-action="close"]')?.focus({ preventScroll: true });
}

/**
 * Lignes propres à une Corrupted GachaDice (A94, A101) : prix du prochain dé
 * en or (`corruptedUnitCost` : base fixe, +0,1 % par dé tiré, remis à neuf à
 * la mort), puis le compteur « N dés déjà tirés ici ».
 */
/** Hausse du prix par dé tiré, en pourcent (`corruptedCostGrowth`, ×1,001 → « 0,1 »). */
function growthPercent(): string {
  const raw = (BALANCE as unknown as Record<string, unknown>).corruptedCostGrowth;
  const growth = typeof raw === 'number' && Number.isFinite(raw) && raw > 1 ? raw : 1.001;
  return formatNumber((growth - 1) * 100, 2);
}

function corruptedInfoHtml(machine: MachineDef, ctx: ScreenCtx, perDie: number): string {
  const { game } = ctx;
  const unit = corruptedUnitCostOf(game, machine.id) ?? perDie;
  const drawn = corruptedPurchasesOf(game, machine.id);
  const count = drawn === 0 ? 'Aucun dé tiré ici' : `${formatGold(drawn)} dé${drawn > 1 ? 's' : ''} déjà tiré${drawn > 1 ? 's' : ''} ici`;
  return `
      <p class="machine-per" data-role="corrupted-per" data-cost="${Math.round(unit)}"><b class="num">${formatGold(unit)}</b> or le dé</p>
      <p class="machine-exp" data-role="corrupted-growth">Prix +${growthPercent()} % par dé tiré ici, remis à neuf à la mort</p>
      <p class="machine-count" data-role="corrupted-count" data-drawn="${drawn}">${count}</p>`;
}

function buildMachineSection(machine: MachineDef, ctx: ScreenCtx, refresh: () => void): HTMLElement {
  const { game } = ctx;
  const stats = game.getDerivedStats();
  const unlocked = isMachineUnlocked(game.state.nodes, machine);
  const perDie = game.gachaCost(machine.id, 1);
  const corrupted = isCorruptedMachine(machine);

  const section = document.createElement('section');
  section.className = `machine-card f-panel${unlocked ? '' : ' machine-card--locked'}${corrupted ? ' machine-card--corrupted' : ''}`;
  if (corrupted) section.setAttribute('data-corrupted', 'true');
  // La spec e2e et la CSS ciblent la machine par son identifiant : les cartes
  // sont rendues génériquement depuis `src/data/machines.ts` (v0.7.0 : la v12
  // arrive de là, personne n'a de liste en dur).
  section.setAttribute('data-machine', machine.id);
  const nodeName = machine.requiresNode ? (NODE_BY_ID[machine.requiresNode]?.name ?? machine.requiresNode) : '';
  section.innerHTML = `
    <div class="machine-vig">${dieArt(machine.sides, topRarity(machine, stats.rarityWeightMult), {
      size: 58,
      locked: !unlocked,
    })}${unlocked ? '' : `<span class="machine-vig-lock">${icon('lock')}</span>`}</div>
    <div class="machine-info">
      <h2 class="machine-name"><span>${machine.name}</span><span class="machine-sides" data-role="machine-sides">${dieSidesLabel(
        machine.sides,
      )}</span></h2>
      ${
        corrupted
          ? corruptedInfoHtml(machine, ctx, perDie)
          : `<p class="machine-per"><b class="num">${formatGold(perDie)}</b> or le dé</p>`
      }
      ${oddsHtml(machine, stats.rarityWeightMult)}
    </div>
    ${
      unlocked
        ? '<div class="machine-lots"></div>'
        : `<p class="machine-locked" data-role="machine-locked">${icon('lock')}<span>Débloquer dans l’Arbre${
            nodeName ? ` (${nodeName})` : ''
          }</span></p>`
    }
  `;

  const lotsHost = section.querySelector<HTMLElement>('.machine-lots');
  if (lotsHost && unlocked) {
    for (const qty of GACHA_LOTS) {
      const cost = game.gachaCost(machine.id, qty);
      const affordable = game.state.gold >= cost;
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'f-btn f-lot btn-lot';
      btn.disabled = !affordable;
      btn.setAttribute('aria-label', `Acheter ${qty} dé${qty > 1 ? 's' : ''} pour ${formatGold(cost)} or`);
      btn.innerHTML = `<span class="lot-qty">×${qty}</span><span class="lot-cost num">${formatGold(cost)} or</span>`;
      btn.addEventListener('click', () => {
        const dice = game.buyGacha(machine.id, qty);
        if (dice && dice.length > 0) openRevealModal(ctx, dice, refresh);
      });
      lotsHost.appendChild(btn);
    }
  }

  return section;
}

function buildRecycleSection(ctx: ScreenCtx): HTMLElement | null {
  const { game } = ctx;
  if (!game.canRecycle()) return null;
  const commons = game.state.inventory.filter(
    (d) => d.rarity === 'common' && !game.state.equipped.includes(d.id),
  );
  const preview = commons.reduce((sum, d) => sum + recycleValue(d), 0);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.className = 'f-btn f-btn--block shop-recycle';
  btn.setAttribute('data-action', 'recycle-commons');
  btn.disabled = commons.length === 0;
  btn.textContent = `Recycler tous les Communs (+${formatGold(preview)} or)`;
  btn.addEventListener('click', () => game.recycleAllCommon());
  return btn;
}

/**
 * Section « Corrupted GachaDice » (A94) : titre, sous-titre, une carte par
 * machine `corrupted`. `null` tant que la Forge n'existe pas (Artisan des dés).
 */
function buildCorruptedSection(ctx: ScreenCtx, refresh: () => void): HTMLElement | null {
  const machines = corruptedMachinesOf(ctx.game);
  if (machines.length === 0) return null;
  const section = document.createElement('section');
  section.className = 'corrupted-section';
  section.setAttribute('data-role', 'corrupted-section');
  section.setAttribute('aria-labelledby', 'corrupted-title');
  section.innerHTML = `
    <header class="corrupted-head">
      <h2 class="corrupted-title" id="corrupted-title">${icon('abyss')}<span>Corrupted GachaDice</span></h2>
      <p class="corrupted-sub">Dés de niveau 1 avec Matériau · à monter au Trempage</p>
    </header>`;
  for (const machine of machines) section.appendChild(buildMachineSection(machine, ctx, refresh));
  return section;
}

/** Les deux sous-onglets de la Boutique (A98). */
export type ShopSubtab = 'normal' | 'corrupted';

const SUBTAB_KEY = 'diceordie.shop.subtab';
const SUBTAB_LABEL: Record<ShopSubtab, string> = { normal: 'Normal', corrupted: 'Corrupted' };

/**
 * Sous-onglet mémorisé pour la session ; à défaut, `fallback` — « Normal », ou
 * « Corrupted » **en mode infini** (niveau ≥ 11 ; v0.53.0, A103). Un choix du
 * joueur dans la session l'emporte toujours.
 */
export function readShopSubtab(fallback: ShopSubtab = 'normal'): ShopSubtab {
  const stored = readSessionPref(SUBTAB_KEY);
  if (stored === 'corrupted' || stored === 'normal') return stored;
  return fallback;
}

/** Sous-onglet par défaut d'une partie : « Corrupted » au-delà du niveau 10, « Normal » sinon (A103). */
export function defaultShopSubtab(level: number): ShopSubtab {
  return isEndless(level) ? 'corrupted' : 'normal';
}

function writeShopSubtab(tab: ShopSubtab): void {
  writeSessionPref(SUBTAB_KEY, tab);
}

export function renderShopTab(container: HTMLElement, ctx: ScreenCtx, refresh: () => void): void {
  const wrap = document.createElement('div');
  wrap.className = 'shop-tab';
  // Avec la Forge, deux sous-onglets ; sans elle, l'écran d'avant (aucun sous-onglet).
  const split = hasForge(ctx.game);
  const active: ShopSubtab = split ? readShopSubtab(defaultShopSubtab(ctx.game.state.level)) : 'normal';
  wrap.setAttribute('data-subtab-active', active);
  if (split) {
    wrap.appendChild(
      buildSubtabNav<ShopSubtab>({
        tabs: ['normal', 'corrupted'],
        labels: SUBTAB_LABEL,
        active,
        ariaLabel: 'Boutique',
        navRole: 'shop-subtabs',
        tabRole: 'shop-subtab',
        navClass: 'shop-subtabs',
        tabClass: 'shop-subtab',
        onPick: (tab) => {
          writeShopSubtab(tab);
          // Seule la Boutique se redessine : l'onglet reste en place, le défilement repart du haut.
          container.replaceChildren();
          renderShopTab(container, ctx, refresh);
          container.scrollTop = 0;
          container.querySelector<HTMLElement>(`[data-subtab="${tab}"]`)?.focus({ preventScroll: true });
        },
      }),
    );
  }
  if (active === 'normal') {
    // Machines classiques ici ; les Corrupted GachaDice (A94) ont leur sous-onglet (A98).
    for (const machine of (MACHINES as MachineDef[]).filter((m) => !isCorruptedMachine(m))) {
      wrap.appendChild(buildMachineSection(machine, ctx, refresh));
    }
    const recycle = buildRecycleSection(ctx);
    if (recycle) wrap.appendChild(recycle);
  } else {
    const corrupted = buildCorruptedSection(ctx, refresh);
    if (corrupted) wrap.appendChild(corrupted);
  }
  container.appendChild(wrap);
}
